import { useState, useEffect } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_URL

const NIVELES = ['A1','A2','B1','B2','C1','C2']
const NOMBRES_NIVEL = {
  A1:'Rookie', A2:'Explorer', B1:'Challenger', B2:'Elite', C1:'Master', C2:'Legend'
}
const COLORES_NIVEL = {
  A1:'#22C55E', A2:'#3B82F6', B1:'#8B5CF6', B2:'#F59E0B', C1:'#EF4444', C2:'#1B3A8C'
}
const EMOJIS_NIVEL = {
  A1:'🌱', A2:'🚀', B1:'⚡', B2:'🔥', C1:'💎', C2:'👑'
}
const XP_POR_NIVEL = {
  A1:1000, A2:2500, B1:5000, B2:8000, C1:12000, C2:20000
}

// Textos bilingues de esta pantalla. El idioma lo controla el selector
// 🇪🇸 ES / 🇬🇧 EN que ya existe en la barra lateral del Dashboard.
const TEXTOS = {
  es: {
    nivelActual:'Nivel actual', siguienteNivel:'Siguiente nivel', maximo:'Maximo',
    xpTotalesLower:'XP totales', xpTotalesStat:'XP Totales',
    diasRacha:'Dias de racha', maxima:'Maxima',
    leccionesStat:'Lecciones', completadas:'completadas',
    insigniasStat:'Insignias', ganadas:'ganadas',
    xpPorCategoria:'⚡ XP por categoria',
    catLecciones:'Lecciones', catRetos:'Retos', catClases:'Clases', catForos:'VideoForos', catJuegos:'Juegos',
    leccionesComp:'lecciones completadas', retosComp:'retos completados',
    clasesComp:'clases completadas', forosComp:'foros completados', partidasJugadas:'partidas jugadas',
    distribucionXp:'📊 Distribucion de XP',
    sinDatos:'Completa actividades para ver tu distribucion de XP',
    caminoDominio:'🎯 Camino al dominio',
    insigniasGanadas:'🏅 Insignias ganadas',
    completaRetos:'Completa retos para ganar insignias.',
    completadoPct:'completado',
    localeFecha:'es-CO'
  },
  en: {
    nivelActual:'Current level', siguienteNivel:'Next level', maximo:'Maxed out',
    xpTotalesLower:'total XP', xpTotalesStat:'Total XP',
    diasRacha:'Streak days', maxima:'Best',
    leccionesStat:'Lessons', completadas:'completed',
    insigniasStat:'Badges', ganadas:'earned',
    xpPorCategoria:'⚡ XP by category',
    catLecciones:'Lessons', catRetos:'Challenges', catClases:'Classes', catForos:'VideoForums', catJuegos:'Games',
    leccionesComp:'lessons completed', retosComp:'challenges completed',
    clasesComp:'classes completed', forosComp:'forums completed', partidasJugadas:'games played',
    distribucionXp:'📊 XP Distribution',
    sinDatos:'Complete activities to see your XP distribution',
    caminoDominio:'🎯 Path to mastery',
    insigniasGanadas:'🏅 Badges earned',
    completaRetos:'Complete challenges to earn badges.',
    completadoPct:'complete',
    localeFecha:'en-US'
  }
}

function BarraProgreso({ valor, maximo, color, altura = 8 }) {
  const pct = Math.min(Math.round((valor / Math.max(maximo, 1)) * 100), 100)
  return (
    <div style={{ width:'100%', height:altura, background:'#E2E6F0', borderRadius:999, overflow:'hidden' }}>
      <div style={{ width: pct + '%', height:'100%', background:color, borderRadius:999, transition:'width .6s ease' }} />
    </div>
  )
}

function TarjetaStat({ ic, valor, label, color, sublabel }) {
  return (
    <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:'20px 16px', textAlign:'center' }}>
      <div style={{ fontSize:28, marginBottom:6 }}>{ic}</div>
      <div style={{ fontSize:28, fontWeight:900, color: color || '#1A1A2E', marginBottom:2 }}>{valor}</div>
      <div style={{ fontSize:12, color:'#6B7280', fontWeight:600 }}>{label}</div>
      {sublabel && <div style={{ fontSize:11, color:'#9CA3AF', marginTop:2 }}>{sublabel}</div>}
    </div>
  )
}

export default function MiProgreso({ usuario, idioma = 'es' }) {
  const [stats,     setStats]     = useState(null)
  const [insignias, setInsignias] = useState([])
  const [cargando,  setCargando]  = useState(true)
  const t = TEXTOS[idioma] || TEXTOS.es

  useEffect(() => {
    if (!usuario) return
    cargarDatos()
  }, [usuario])

  async function cargarDatos() {
    setCargando(true)
    try {
      const [statsRes, insigniasRes] = await Promise.all([
        axios.get(API + '/progreso/estadisticas/' + usuario.id).catch(() => ({ data: { datos: null } })),
        axios.get(API + '/progreso/insignias/' + usuario.id).catch(() => ({ data: { datos: [] } }))
      ])
      setStats(statsRes.data.datos || {
        xp_total: 0, xp_lecciones: 0, xp_retos: 0,
        xp_clases: 0, xp_foros: 0, xp_juegos: 0,
        racha_dias: 0, racha_maxima: 0,
        lecciones_completadas: 0, clases_completadas: 0,
        foros_completados: 0, retos_completados: 0,
        nivel_actual: 'A1'
      })
      setInsignias(insigniasRes.data.datos || [])
    } catch (err) { console.error(err) }
    finally { setCargando(false) }
  }

  if (cargando) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:60, color:'#6B7280' }}>
      <div style={{ width:32, height:32, border:'3px solid #E2E6F0', borderTopColor:'#7C3AED', borderRadius:'50%', animation:'spinner .8s linear infinite' }} />
    </div>
  )

  const s = stats || {}
  const nivelActual = s.nivel_actual || 'A1'
  const nivelIdx = NIVELES.indexOf(nivelActual)
  const xpNivelActual = nivelIdx > 0 ? XP_POR_NIVEL[NIVELES[nivelIdx - 1]] : 0
  const xpNivelSiguiente = XP_POR_NIVEL[nivelActual] || 20000
  const xpEnNivel = (s.xp_total || 0) - xpNivelActual
  const xpNecesario = xpNivelSiguiente - xpNivelActual
  const pctNivel = Math.min(Math.round((xpEnNivel / Math.max(xpNecesario, 1)) * 100), 100)

  const categorias = [
    { id:'lecciones', ic:'📚', label:t.catLecciones, xp: s.xp_lecciones || 0, completados: s.lecciones_completadas || 0, labelComp:t.leccionesComp, color:'#3B82F6' },
    { id:'retos',     ic:'🔥', label:t.catRetos,     xp: s.xp_retos    || 0, completados: s.retos_completados    || 0, labelComp:t.retosComp,    color:'#7C3AED' },
    { id:'clases',    ic:'🎥', label:t.catClases,    xp: s.xp_clases   || 0, completados: s.clases_completadas   || 0, labelComp:t.clasesComp,   color:'#1B3A8C' },
    { id:'foros',     ic:'🎙', label:t.catForos,     xp: s.xp_foros    || 0, completados: s.foros_completados    || 0, labelComp:t.forosComp,    color:'#059669' },
    { id:'juegos',    ic:'🎮', label:t.catJuegos,    xp: s.xp_juegos   || 0, completados: 0,                         labelComp:t.partidasJugadas, color:'#F59E0B' },
  ]

  const xpTotal = s.xp_total || 0
  const maxXpCategoria = Math.max(...categorias.map(c => c.xp), 1)

  return (
    <div style={{ maxWidth:900, margin:'0 auto', animation:'fadeIn .3s ease both' }}>

      {/* Header nivel actual */}
      <div style={{ background:'linear-gradient(135deg,' + COLORES_NIVEL[nivelActual] + 'dd,' + COLORES_NIVEL[nivelActual] + ')', borderRadius:16, padding:28, marginBottom:24, color:'#fff' }}>
        <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:16 }}>
          <div style={{ fontSize:56 }}>{EMOJIS_NIVEL[nivelActual]}</div>
          <div>
            <div style={{ fontSize:13, opacity:.8, marginBottom:2 }}>{t.nivelActual}</div>
            <div style={{ fontSize:28, fontWeight:900 }}>{nivelActual} — {NOMBRES_NIVEL[nivelActual]}</div>
            <div style={{ fontSize:14, opacity:.8 }}>{xpTotal.toLocaleString()} {t.xpTotalesLower}</div>
          </div>
          <div style={{ marginLeft:'auto', textAlign:'right' }}>
            <div style={{ fontSize:13, opacity:.8 }}>{t.siguienteNivel}</div>
            <div style={{ fontSize:20, fontWeight:800 }}>
              {nivelIdx < NIVELES.length - 1 ? NIVELES[nivelIdx + 1] + ' ' + EMOJIS_NIVEL[NIVELES[nivelIdx + 1]] : t.maximo + ' 👑'}
            </div>
          </div>
        </div>
        <div style={{ marginBottom:6 }}>
          <BarraProgreso valor={xpEnNivel} maximo={xpNecesario} color='rgba(255,255,255,0.9)' altura={10} />
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, opacity:.8 }}>
          <span>{xpEnNivel.toLocaleString()} XP</span>
          <span>{pctNivel}% {t.completadoPct}</span>
          <span>{xpNecesario.toLocaleString()} XP</span>
        </div>
      </div>

      {/* Stats rapidas */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:24 }}>
        <TarjetaStat ic='⚡' valor={xpTotal.toLocaleString()} label={t.xpTotalesStat} color='#F59E0B' />
        <TarjetaStat ic='🔥' valor={s.racha_dias || 0} label={t.diasRacha} sublabel={t.maxima + ': ' + (s.racha_maxima || 0)} color='#EF4444' />
        <TarjetaStat ic='📚' valor={s.lecciones_completadas || 0} label={t.leccionesStat} sublabel={t.completadas} color='#3B82F6' />
        <TarjetaStat ic='🏅' valor={insignias.length} label={t.insigniasStat} sublabel={t.ganadas} color='#7C3AED' />
      </div>

      {/* XP por categoria */}
      <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:24, marginBottom:24 }}>
        <h2 style={{ fontSize:16, fontWeight:800, color:'#1A1A2E', marginBottom:20 }}>{t.xpPorCategoria}</h2>
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {categorias.map(cat => (
            <div key={cat.id}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:18 }}>{cat.ic}</span>
                  <span style={{ fontSize:14, fontWeight:600, color:'#374151' }}>{cat.label}</span>
                  <span style={{ fontSize:11, color:'#9CA3AF' }}>{cat.completados} {cat.labelComp}</span>
                </div>
                <span style={{ fontSize:14, fontWeight:800, color:cat.color }}>{cat.xp.toLocaleString()} XP</span>
              </div>
              <BarraProgreso valor={cat.xp} maximo={maxXpCategoria} color={cat.color} altura={10} />
            </div>
          ))}
        </div>
      </div>

      {/* Distribucion y camino al dominio */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:24 }}>
        <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:24 }}>
          <h2 style={{ fontSize:16, fontWeight:800, color:'#1A1A2E', marginBottom:16 }}>{t.distribucionXp}</h2>
          {xpTotal === 0 ? (
            <div style={{ textAlign:'center', padding:'20px 0', color:'#9CA3AF', fontSize:13 }}>
              {t.sinDatos}
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {categorias.filter(c => c.xp > 0).map(cat => {
                const pct = Math.round((cat.xp / xpTotal) * 100)
                return (
                  <div key={cat.id} style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:12, height:12, borderRadius:'50%', background:cat.color, flexShrink:0 }} />
                    <div style={{ flex:1, fontSize:13, color:'#374151' }}>{cat.label}</div>
                    <div style={{ fontSize:13, fontWeight:700, color:cat.color }}>{pct}%</div>
                    <div style={{ width:80 }}>
                      <BarraProgreso valor={cat.xp} maximo={xpTotal} color={cat.color} altura={6} />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:24 }}>
          <h2 style={{ fontSize:16, fontWeight:800, color:'#1A1A2E', marginBottom:16 }}>{t.caminoDominio}</h2>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {NIVELES.map((n, i) => {
              const xpMin = i > 0 ? XP_POR_NIVEL[NIVELES[i-1]] : 0
              const xpMax = XP_POR_NIVEL[n]
              const completado = xpTotal >= xpMax
              const enCurso = nivelActual === n
              const bloqueado = NIVELES.indexOf(nivelActual) < i && !completado
              return (
                <div key={n} style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ width:32, height:32, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, flexShrink:0,
                    background: completado ? COLORES_NIVEL[n] : enCurso ? COLORES_NIVEL[n] + '33' : '#F4F6FA',
                    border: '2px solid ' + (completado || enCurso ? COLORES_NIVEL[n] : '#E2E6F0') }}>
                    {completado ? '✓' : EMOJIS_NIVEL[n]}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
                      <span style={{ fontSize:12, fontWeight:700, color: bloqueado ? '#9CA3AF' : '#374151' }}>
                        {n} {NOMBRES_NIVEL[n]}
                      </span>
                      <span style={{ fontSize:11, color:'#9CA3AF' }}>{xpMax.toLocaleString()} XP</span>
                    </div>
                    <BarraProgreso
                      valor={completado ? xpMax : enCurso ? xpTotal - xpMin : 0}
                      maximo={xpMax - xpMin}
                      color={COLORES_NIVEL[n]}
                      altura={5}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Insignias */}
      <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:24 }}>
        <h2 style={{ fontSize:16, fontWeight:800, color:'#1A1A2E', marginBottom:16 }}>{t.insigniasGanadas}</h2>
        {insignias.length === 0 ? (
          <div style={{ textAlign:'center', padding:'32px 0', color:'#9CA3AF' }}>
            <div style={{ fontSize:48, marginBottom:12 }}>🏅</div>
            <p style={{ fontSize:14 }}>{t.completaRetos}</p>
          </div>
        ) : (
          <div style={{ display:'flex', flexWrap:'wrap', gap:12 }}>
            {insignias.map((ins, i) => (
              <div key={i} style={{ background:'linear-gradient(135deg,#7C3AED,#4F46E5)', borderRadius:12, padding:'12px 16px', color:'#fff', display:'flex', alignItems:'center', gap:10, minWidth:160 }}>
                <span style={{ fontSize:28 }}>{ins.insignia_emoji}</span>
                <div>
                  <div style={{ fontSize:13, fontWeight:700 }}>{ins.insignia_nombre}</div>
                  <div style={{ fontSize:11, opacity:.7 }}>
                    {ins.ganada_en ? new Date(ins.ganada_en).toLocaleDateString(t.localeFecha) : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
